import { FC, useState } from 'react';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb, SCUITextFieldWithButton } from '../../atomic/molecules';
import Avatar from '@mui/material/Avatar';
import { SCUICollapsiblePaper, SCUICustomDataGrid } from '../../atomic/molecules';
import { Box, Chip, Grid, Link, Stack, Tooltip, Typography } from '@mui/material';
import { StoreQuery } from '../../services/query';
import { useTranslation } from 'react-i18next';
import './StoreLcwaikikiV2.scss';
import { SCUILoadingLinear, SCUINoRecordComponent } from '../../atomic/atoms';
import CopyToClipboardButton from '../../atomic/atoms/SCUICopyToClipboardButton/SCUICopyToClipboardButton';
import Swal from 'sweetalert2';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaletteIcon from '@mui/icons-material/Palette';
import StraightenIcon from '@mui/icons-material/Straighten';
import LabelIcon from '@mui/icons-material/Label';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { SLV2 } from './storeLcwaikikiV2.classes';

const chipSx = {
    fontSize: 12,
    height: 28,
    fontWeight: 500,
    backgroundColor: 'rgba(25, 118, 210, 0.06)',
    color: 'text.primary',
    border: '1px solid rgba(25, 118, 210, 0.15)',
    '& .MuiChip-icon': { fontSize: 14, color: 'primary.main' },
    '& .MuiChip-label': { px: 1.25 },
};

export const StoreLcwaikikiV2: FC<any> = () => {
    const [siparisNo, setSiparisNo] = useState('');
    const [billData, setBillData] = useState<any>();
    const [orderNo, setOrderNo] = useState<any>();
    const [displayPaperContent, setDisplayPaperContent] = useState(false);
    const [barDisplay, setBarDisplay] = useState(false);
    const { t } = useTranslation();
    const other_link: any = import.meta.env.VITE_APPURL!;

    const columns = [
        {
            field: 'imageUrl',
            headerName: t('general.image'),
            width: 130,
            renderCell: (params: any) => {
                return (
                    <Tooltip title="LCW.com'da gör" placement="top" arrow>
                        <Avatar
                            className={`${SLV2.cellProductImage} ${SLV2.cellProductImageLink}`}
                            data-model-id={params.row.modelId}
                            data-option-id={params.row.optionId}
                            src={params.row.imageUrl}
                            sx={{
                                width: 100,
                                height: 100,
                                cursor: 'pointer',
                                borderRadius: 2.5,
                                transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                '&:hover': {
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
                                    transform: 'scale(1.05)',
                                },
                            }}
                            variant="rounded"
                            onClick={() =>
                                window.open(
                                    'https://www.lcw.com/model/?modelId=' +
                                        params.row.modelId +
                                        '&optionId=' +
                                        params.row.optionId,
                                    '_blank',
                                    'noopener,noreferrer'
                                )
                            }
                        />
                    </Tooltip>
                );
            },
        },
        {
            field: 'urunBilgi',
            minWidth: 480,
            flex: 1,
            headerAlign: 'center' as const,
            headerName: t('general.productInformation'),
            renderCell: (params: any) => (
                <Box className={SLV2.cellProductInfo} sx={{ py: 1.5, px: 1 }}>
                    <Typography
                        className={SLV2.cellProductTitle}
                        variant="body2"
                        fontWeight={700}
                        fontSize={13}
                        color="text.primary"
                        sx={{ mb: 1.25, lineHeight: 1.4 }}
                    >
                        {params.row.productDescriptionOnWebSite}
                        {params.row.optionTitleKey ? ` — ${params.row.optionTitleKey}` : ''}
                    </Typography>
                    <Box className={SLV2.cellProductChips} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        <Chip
                            className={SLV2.cellProductCodeChip}
                            icon={<QrCodeIcon />}
                            label={`${params.row.ozelKod1} - ${params.row.renkKod}`}
                            size="small"
                            sx={chipSx}
                            data-product-code={params.row.ozelKod1}
                            data-color-code={params.row.renkKod}
                        />
                        <Chip
                            className={SLV2.cellProductColorChip}
                            icon={<PaletteIcon />}
                            label={params.row.colorName}
                            size="small"
                            sx={chipSx}
                            data-color-name={params.row.colorName}
                        />
                        <Chip
                            className={SLV2.cellProductSizeChip}
                            icon={<StraightenIcon />}
                            label={params.row.size}
                            size="small"
                            sx={chipSx}
                            data-size={params.row.size}
                        />
                        <Chip
                            className={SLV2.cellProductBrandChip}
                            icon={<LabelIcon />}
                            label={params.row.brandName}
                            size="small"
                            sx={chipSx}
                            data-brand-name={params.row.brandName}
                        />
                    </Box>
                </Box>
            ),
        },
        {
            field: 'quantity',
            flex: 1,
            maxWidth: 90,
            headerName: t('general.quantity'),
            renderCell: (params: any) => (
                <Typography
                    className={SLV2.cellQuantity}
                    fontWeight={700}
                    fontSize={15}
                    color="text.primary"
                    data-quantity={params.row.quantity}
                >
                    {params.row.quantity}
                </Typography>
            ),
        },
        {
            field: 'unitPrice',
            flex: 1,
            maxWidth: 110,
            headerName: t('general.price'),
            renderCell: (params: any) => (
                <Typography
                    className={SLV2.cellUnitPrice}
                    fontWeight={500}
                    fontSize={13}
                    color="text.secondary"
                    data-unit-price={params.row.unitPrice}
                >
                    {params.row.unitPrice}
                </Typography>
            ),
        },
        {
            field: 'salePrice',
            flex: 1,
            maxWidth: 110,
            headerName: t('general.cost'),
            renderCell: (params: any) => (
                <Typography
                    className={SLV2.cellSalePrice}
                    fontWeight={700}
                    fontSize={14}
                    color="primary.main"
                    data-sale-price={params.row.salePrice}
                >
                    {params.row.salePrice}
                </Typography>
            ),
        },
    ];

    const fatura = (orderId: string, shippingListId: string, customerId: string) => {
        let params = {
            orderId: orderId,
            shippingListId: shippingListId,
            customerId: customerId,
        };
        StoreQuery.GetStoreInvoiceFile(params);
    };

    const siparisAra = () => {
        try {
            if (!/^\d+$/.test(siparisNo.trim())) {
                Swal.fire(t('general.validFieldControlText'), ' ', 'error');
                return;
            }
            setBarDisplay(true);
            let params: any = {};
            params.OrderNo = siparisNo.trim();
            StoreQuery.GetStoreSaleOrderByOrderNoV2(params).then((res: any) => {
                if (res.data) {
                    if (
                        res.data.shippingListForBackofficeModels &&
                        res.data.shippingListForBackofficeModels.length === 1
                    ) {
                        setDisplayPaperContent(true);
                    } else {
                        setDisplayPaperContent(false);
                    }

                    let bills =
                        res.data.shippingListForBackofficeModels &&
                        res.data.shippingListForBackofficeModels.map((bill: any) => {
                            return {
                                orderId: res.data.orderId || '-',
                                shippingListNo: bill.shippingListNo || '-',
                                shippingListId: bill.shippingListId || '-',
                                customerId: res.data.customerId || '-',
                                shippingListItemForBackofficeModels: bill.shippingListItemForBackofficeModels,
                            };
                        });
                    let orderNo = res.data.orderNumber;
                    setOrderNo(orderNo);
                    setBillData(bills);
                    setBarDisplay(false);
                } else {
                    Swal.fire(t('general.notFound'), ' ', 'error');
                    setBarDisplay(false);
                }
            });
        } catch (error) {
            setBarDisplay(false);
            console.log('Hata :>> ', error);
        }
    };

    return (
        <SCUIContent className={SLV2.page}>
            <Grid direction="row" className={SLV2.root}>
                <Box className={SLV2.breadcrumb}>
                    <SCUIBreadCrumb title={t('storeLcwaikiki.title')} componentLink={'storelcwaikikiV2'} />
                </Box>
                <form
                    className={SLV2.searchForm}
                    onSubmit={(e: any) => {
                        e.preventDefault();
                        siparisAra();
                    }}
                >
                    <SCUITextFieldWithButton
                        paperClassName={SLV2.searchBox}
                        inputClassName={SLV2.inputOrderNo}
                        iconButtonClassName={SLV2.btnSearch}
                        placeholder={t('general.orderNo')}
                        onChange={(event: any) => {
                            setSiparisNo(event.target.value);
                        }}
                        onClick={() => siparisAra()}
                        onKeyPress={(e: any) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                siparisAra();
                            }
                        }}
                    />
                </form>

                <Box className={SLV2.loading}>
                    <SCUILoadingLinear display={barDisplay} />
                </Box>

                <Grid container sx={{ mt: 2, width: '100%' }} className={SLV2.results}>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            lg: 12,
                            xl: 12,
                        }}
                        className={SLV2.resultsGrid}
                        sx={{ width: '100%', minWidth: 0 }}
                    >
                        {billData ? (
                            <Stack spacing={2} className={SLV2.billStack}>
                                {billData.map((bill: any) => (
                                    <SCUICollapsiblePaper
                                        key={bill.shippingListId}
                                        open={displayPaperContent}
                                        changeColorWhenOpen
                                        paperText={
                                            <Box
                                                className={SLV2.billHeader}
                                                data-shipping-list-id={bill.shippingListId}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 1.5,
                                                    py: 0.25,
                                                }}
                                            >
                                                <Box
                                                    className={SLV2.billShippingInfo}
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                                                >
                                                    <LocalShippingIcon
                                                        className={SLV2.billShippingIcon}
                                                        sx={{ fontSize: 17, color: 'text.secondary' }}
                                                    />
                                                    <Typography
                                                        className={SLV2.billShippingLabel}
                                                        variant="caption"
                                                        fontWeight={700}
                                                        fontSize={12}
                                                        color="text.secondary"
                                                        sx={{ letterSpacing: 0.2 }}
                                                    >
                                                        {t('general.shipping')} No:
                                                    </Typography>
                                                    <Link
                                                        className={SLV2.billShippingLink}
                                                        href={other_link + '/orderdetail?orderNumber=' + orderNo}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        underline="hover"
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: 14,
                                                            color: 'primary.main',
                                                            letterSpacing: 0.2,
                                                        }}
                                                        data-shipping-list-no={bill.shippingListNo}
                                                    >
                                                        {bill.shippingListNo}
                                                    </Link>
                                                    <Box className={SLV2.billCopyButtonBox}>
                                                        <CopyToClipboardButton textToCopy={bill.shippingListNo} />
                                                    </Box>
                                                </Box>

                                                <Chip
                                                    className={SLV2.btnDownloadInvoice}
                                                    icon={<FileDownloadIcon />}
                                                    label={`${t('general.bill')} ${t('general.download')}`}
                                                    size="small"
                                                    clickable
                                                    onClick={() =>
                                                        fatura(bill.orderId, bill.shippingListId, bill.customerId)
                                                    }
                                                    data-order-id={bill.orderId}
                                                    data-shipping-list-id={bill.shippingListId}
                                                    sx={{
                                                        fontSize: 12,
                                                        height: 28,
                                                        fontWeight: 600,
                                                        backgroundColor: 'rgba(25, 118, 210, 0.07)',
                                                        color: 'primary.main',
                                                        border: '1px solid rgba(25, 118, 210, 0.25)',
                                                        '& .MuiChip-icon': {
                                                            color: 'primary.main',
                                                            fontSize: 15,
                                                        },
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: 'primary.main',
                                                            color: 'white',
                                                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                                            '& .MuiChip-icon': { color: 'white' },
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        }
                                    >
                                        <Box
                                            className={`${SLV2.billPanel} ${SLV2.billItemsGrid}`}
                                            sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}
                                        >
                                            <SCUICustomDataGrid
                                                sx={{
                                                    border: 0,
                                                    width: '100%',
                                                    '& .MuiDataGrid-columnHeaders': {
                                                        backgroundColor: 'grey.50',
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                    },
                                                    '& .MuiDataGrid-row': {
                                                        transition: 'background-color 0.15s ease',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                        },
                                                    },
                                                }}
                                                randomId
                                                rows={bill.shippingListItemForBackofficeModels}
                                                columns={columns}
                                                hideFooter
                                                rowHeight={130}
                                            />
                                        </Box>
                                    </SCUICollapsiblePaper>
                                ))}
                            </Stack>
                        ) : (
                            <Box className={SLV2.emptyState}>
                                <SCUINoRecordComponent />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </SCUIContent>
    );
};
